/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import {
    type ComponentProps,
    type ComponentPropsWithoutRef,
    cloneElement,
    Fragment,
    type HTMLAttributes,
    isValidElement,
    type ReactNode,
    Suspense,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Await } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { NavLink } from '@/components/link';
import type { ShopperProducts } from '@/scapi';
import { cn } from '@/lib/utils';
// ## SEO url changes ##
// ## SEO url changes ##
import { toCategoryUrl, rewriteLegacyCategoryUrl } from '@/lib/seo/url-helpers';
// ## SEO url changes ##
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import {
    createSubCategoryStore,
    SubCategoryContext,
    type SubCategoryStore,
    useSubCategory,
} from '@/components/navigation-menu-responsive/context';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    NavigationMenuViewport,
    navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';

export type CategoryNavigationMenuDesktopListCtx = {
    level: number;
    categories: ShopperProducts.schemas['Category'][];
    parent: ShopperProducts.schemas['Category'] | undefined;
    path: ReadonlyArray<ShopperProducts.schemas['Category']>;
};

export type CategoryNavigationMenuDesktopListItemCtx = {
    category: ShopperProducts.schemas['Category'];
    parent: ShopperProducts.schemas['Category'] | undefined;
    level: number;
    path: ReadonlyArray<ShopperProducts.schemas['Category']>;
    index: number;
    isFirst: boolean;
    isLast: boolean;
    isLeaf: boolean;
};

type SlotType<T> = ReactNode | ((ctx: T) => ReactNode);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PropsSlot<T, P extends Record<string, any> = Record<string, any>> = P | ((ctx: T) => P | undefined);

export type CategoryNavigationMenuDesktopProps = ComponentPropsWithoutRef<typeof NavigationMenu> & {
    categories?: ShopperProducts.schemas['Category'][];

    // Maximum depth (0=no display, 1=only top-level categories, ...). Default: unlimited.
    maxDepth?: number;

    // Optional element props customizations.
    propsViewport?: PropsSlot<CategoryNavigationMenuDesktopListCtx, ComponentProps<typeof NavigationMenuViewport>>;
    propsList?: PropsSlot<CategoryNavigationMenuDesktopListCtx, ComponentProps<typeof NavigationMenuList>>;
    propsListItem?: PropsSlot<CategoryNavigationMenuDesktopListItemCtx, ComponentProps<typeof NavigationMenuItem>>;
    propsContentContainer?: PropsSlot<
        CategoryNavigationMenuDesktopListItemCtx,
        ComponentProps<typeof NavigationMenuContent>
    >;
    propsContent?: PropsSlot<CategoryNavigationMenuDesktopListItemCtx, ComponentProps<'div'>>;
    propsElement?: PropsSlot<CategoryNavigationMenuDesktopListItemCtx, ComponentProps<typeof NavigationMenuLink>>;

    // Optional render customizations.
    renderElement?: SlotType<CategoryNavigationMenuDesktopListItemCtx>;
    renderSlotContentAfter?: SlotType<CategoryNavigationMenuDesktopListItemCtx>;
    renderSlotListBefore?: SlotType<CategoryNavigationMenuDesktopListCtx>;
    renderSlotListAfter?: SlotType<CategoryNavigationMenuDesktopListCtx>;
    renderSlotListItemBefore?: SlotType<CategoryNavigationMenuDesktopListItemCtx>;
    renderSlotListItemAfter?: SlotType<CategoryNavigationMenuDesktopListItemCtx>;
};

type CategoryNavigationMenuDesktopChildProps = {
    categories: ShopperProducts.schemas['Category'][];
};

type WithCategoryNavigationMenuProps = {
    children?: ReactNode | ((props: CategoryNavigationMenuDesktopChildProps) => ReactNode);
    resolve?: Promise<ShopperProducts.schemas['Category']>;
    defer?: Promise<ShopperProducts.schemas['Category'][]>;
    fallback?: ReactNode;
    errorElement?: ReactNode;
    // Programmatically filter out items that you do not want to show. Default: 'c_showInMenu'.
    itemsFilter?:
        | keyof ShopperProducts.schemas['Category']
        | ((category: ShopperProducts.schemas['Category']) => boolean);
};

type ColumnGroup = {
    column: number;
    categories: ShopperProducts.schemas['Category'][];
};

type NavigationOverrideEntry = {
    title?: unknown;
    url?: unknown;
    banner?: unknown;
    bannerLink?: unknown;
    mobileIcon?: unknown;
    topLevelLinkLogo?: unknown;
    column?: unknown;
    children?: unknown;
    isMarketingBanner?: unknown;
};

type NavigationOverrideCategory = ShopperProducts.schemas['Category'] & {
    c_navigationOverride?: string;
    c_navigationOverrideColumn?: unknown;
    c_navigationOverrideUrl?: string;
    c_alternativeUrl?: string;
    c_isHeaderOnly?: unknown;
    c_navigationBannerImage?: string;
    c_navigationBannerLink?: string;
    c_mobileIcon?: string;
    c_topLevelLinkLogo?: string;
    c_isMarketingBanner?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function propsFor<T, P extends Record<string, any> = Record<string, any>>(
    input: PropsSlot<T, P> | undefined,
    ctx: T
): P {
    return typeof input === 'function' ? (input(ctx) ?? ({} as P)) : (input ?? ({} as P));
}

function renderSlot<T>(slot: SlotType<T> | undefined, ctx: T) {
    return typeof slot === 'function' ? (slot(ctx) ?? null) : (slot ?? null);
}

function filterItem(
    category: ShopperProducts.schemas['Category'],
    itemsFilter: WithCategoryNavigationMenuProps['itemsFilter']
): boolean {
    if (typeof itemsFilter === 'function') {
        return Boolean(itemsFilter(category));
    }
    if (itemsFilter) {
        return Boolean(category[itemsFilter]);
    }
    return isCategoryVisible(category);
}

function WithCategoryNavigationMenuView({
    root: rootCategory,
    defer: subCategoriesPromise,
    itemsFilter,
    children,
}: Omit<WithCategoryNavigationMenuProps, 'resolve' | 'fallback' | 'errorElement'> & {
    root?: ShopperProducts.schemas['Category'];
}) {
    const hasRootNavigationOverride =
        rootCategory !== undefined && getNavigationOverrideEntries(rootCategory).length > 0;

    // Stable root-level categories — these never change after first render, so the items
    // rendered against this list keep referential identity. Subcategory enrichment is
    // delivered via the SubCategoryStore below; consumers subscribe selectively via
    // `useSubCategory(id)` so only the subtree for an enriched category re-renders.
    const rootCategories = useMemo(() => getRootCategories(rootCategory, itemsFilter), [rootCategory, itemsFilter]);

    // Stable store instance — the ref ensures the same store object is shared with all
    // descendants for the lifetime of this component. Updates to the store fire via the
    // store's own subscribe/notify, not React state, so `<SubCategoryContext>` never
    // re-renders its consumer tree by virtue of the context value changing.
    const storeRef = useRef<SubCategoryStore | null>(null);
    if (!storeRef.current) {
        storeRef.current = createSubCategoryStore();
    }

    useEffect(() => {
        if (hasRootNavigationOverride) {
            return;
        }

        // Once the subcategories promise resolves, push enriched data into the store.
        // Components that subscribed via `useSubCategory(id)` will re-render selectively.
        void subCategoriesPromise?.then((subCategories: ShopperProducts.schemas['Category'][]) => {
            storeRef.current?.update(
                new Map(
                    subCategories.map((category: ShopperProducts.schemas['Category']) => [
                        category.id,
                        {
                            ...category,
                            categories: category.categories?.filter(
                                (childCategory: ShopperProducts.schemas['Category']) =>
                                    filterItem(childCategory, itemsFilter)
                            ),
                        },
                    ])
                )
            );
        });
    }, [subCategoriesPromise, hasRootNavigationOverride, itemsFilter]);

    function renderChildren() {
        if (isValidElement<CategoryNavigationMenuDesktopChildProps>(children)) {
            return cloneElement(children, { categories: rootCategories });
        }
        if (typeof children === 'function') {
            return (children as (props: CategoryNavigationMenuDesktopChildProps) => ReactNode)({
                categories: rootCategories,
            });
        }
        return children;
    }

    return <SubCategoryContext value={storeRef.current}>{renderChildren()}</SubCategoryContext>;
}

function isCategoryVisible(category: ShopperProducts.schemas['Category']): boolean {
    return category.c_showInMenu !== false;
}

function getVisibleCategories(categories: ShopperProducts.schemas['Category'][] | undefined) {
    return Array.isArray(categories) ? categories.filter(isCategoryVisible) : [];
}

function getRootCategories(
    rootCategory: ShopperProducts.schemas['Category'] | undefined,
    itemsFilter: WithCategoryNavigationMenuProps['itemsFilter']
): ShopperProducts.schemas['Category'][] {
    if (!rootCategory) {
        return [];
    }

    const overrideEntries = getNavigationOverrideEntries(rootCategory);

    if (overrideEntries.length > 0) {
        return overrideEntries
            .map((entry, index) => normalizeNavigationOverrideEntry(entry, index, rootCategory.id))
            .filter((overrideCategory): overrideCategory is ShopperProducts.schemas['Category'] =>
                Boolean(overrideCategory)
            )
            .filter((category) => filterItem(category, itemsFilter));
    }

    return (rootCategory.categories ?? []).filter((category: ShopperProducts.schemas['Category']) =>
        filterItem(category, itemsFilter)
    );
}

function isNavigationOverrideEntry(value: unknown): value is NavigationOverrideEntry {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getNavigationOverrideEntries(category: ShopperProducts.schemas['Category']): NavigationOverrideEntry[] {
    const navigationOverride = (category as NavigationOverrideCategory).c_navigationOverride;

    if (navigationOverride == null) {
        return [];
    }

    if (typeof navigationOverride !== 'string') {
        throw new Error('Expected c_navigationOverride to be a stringified JSON array.');
    }

    if (navigationOverride.trim() === '') {
        return [];
    }

    const parsedNavigationOverride: unknown = JSON.parse(navigationOverride);

    if (!Array.isArray(parsedNavigationOverride)) {
        throw new Error('Expected c_navigationOverride to parse to an array.');
    }

    return parsedNavigationOverride.filter(isNavigationOverrideEntry);
}

function normalizeNavigationOverrideEntry(
    entry: NavigationOverrideEntry,
    index: number,
    parentId: string
): ShopperProducts.schemas['Category'] | null {
    const title = typeof entry.title === 'string' ? entry.title.trim() : '';
    const url = typeof entry.url === 'string' ? entry.url.trim() : '';
    const banner = typeof entry.banner === 'string' ? entry.banner.trim() : '';
    const bannerLink = typeof entry.bannerLink === 'string' ? entry.bannerLink.trim() : '';
    const mobileIcon = typeof entry.mobileIcon === 'string' ? entry.mobileIcon.trim() : '';
    const topLevelLinkLogo = typeof entry.topLevelLinkLogo === 'string' ? entry.topLevelLinkLogo.trim() : mobileIcon;

    if (!title) {
        return null;
    }

    const rawChildren = Array.isArray(entry.children) ? entry.children : [];
    const categories = rawChildren
        .filter(isNavigationOverrideEntry)
        .map((childEntry, childIndex) =>
            normalizeNavigationOverrideEntry(childEntry, childIndex, `${parentId}-${index}`)
        )
        .filter((childCategory): childCategory is ShopperProducts.schemas['Category'] => Boolean(childCategory));

    return {
        id: `${parentId}-navigation-override-${index}`,
        name: title,
        categories,
        onlineSubCategoriesCount: categories.length,
        c_showInMenu: true,
        c_navigationOverrideColumn: entry.column,
        c_navigationOverrideUrl: url,
        ...(banner ? { c_navigationBannerImage: banner } : {}),
        ...(bannerLink ? { c_navigationBannerLink: bannerLink } : {}),
        ...(mobileIcon ? { c_mobileIcon: mobileIcon } : {}),
        ...(topLevelLinkLogo ? { c_topLevelLinkLogo: topLevelLinkLogo } : {}),
        ...(entry.isMarketingBanner === true ? { c_isMarketingBanner: true } : {}),
    } as NavigationOverrideCategory;
}

function getNavigationChildren(category: ShopperProducts.schemas['Category']): ShopperProducts.schemas['Category'][] {
    const overrideEntries = getNavigationOverrideEntries(category);

    if (overrideEntries.length > 0) {
        return overrideEntries
            .map((entry, index) => normalizeNavigationOverrideEntry(entry, index, category.id))
            .filter((overrideCategory): overrideCategory is ShopperProducts.schemas['Category'] =>
                Boolean(overrideCategory)
            );
    }

    return getVisibleCategories(category.categories);
}

function hasChildren(category: ShopperProducts.schemas['Category']): boolean {
    if (getNavigationChildren(category).length > 0) {
        return true;
    }
    // Top-level root children arrive from the `root` fetch at depth=1 — they don't include
    // their own `categories[]` until the `subs` promise resolves and the SubCategoryStore
    // hydrates them. The SCAPI `onlineSubCategoriesCount` field IS populated at depth=1
    // (the loader uses it to decide which subtrees to fetch), so we use it as a static
    // "may have children" signal so the trigger-vs-link decision renders correctly on first
    // paint. The panel itself reads enriched data via `useSubCategory(id)`.
    return typeof category.onlineSubCategoriesCount === 'number' && category.onlineSubCategoriesCount > 0;
}

function getDesktopNavColumn(category: ShopperProducts.schemas['Category']): number {
    const column = Number(
        (category as NavigationOverrideCategory).c_navigationOverrideColumn ?? category.c_desktopNavColumn ?? 1
    );
    return Number.isFinite(column) && column >= 1 ? Math.floor(column) : 1;
}

// ## SEO url changes ## — allowOverride=true keeps BM URLs as-is; false (default) rewrites /category/ → /c/
function getCategoryUrl(
    config: AppConfig,
    category: ShopperProducts.schemas['Category'],
    allowOverride: boolean
): string {
    const cat = category as NavigationOverrideCategory;
    const stored = cat.c_navigationOverrideUrl ?? cat.c_alternativeUrl;
    if (!stored) return toCategoryUrl(config, category);
    return allowOverride ? stored : rewriteLegacyCategoryUrl(stored);
}

function isCategoryLinkable(category: ShopperProducts.schemas['Category']): boolean {
    const navigationOverrideUrl = (category as NavigationOverrideCategory).c_navigationOverrideUrl;

    return (category as NavigationOverrideCategory).c_isHeaderOnly !== true && navigationOverrideUrl !== '';
}

function withoutInteractiveClassNames(className: string | undefined): string | undefined {
    return className
        ?.split(/\s+/)
        .filter(Boolean)
        .filter(
            (token) =>
                !token.startsWith('hover:') &&
                !token.startsWith('focus:') &&
                !token.startsWith('focus-visible:') &&
                !token.includes(':hover:') &&
                !token.includes(':focus:') &&
                !token.includes(':focus-visible:')
        )
        .join(' ');
}

function groupCategoriesByColumn(categories: ShopperProducts.schemas['Category'][]): ColumnGroup[] {
    const maxColumn = categories.reduce((max, category) => Math.max(max, getDesktopNavColumn(category)), 1);
    const groups = Array.from({ length: maxColumn }, (_, index) => ({
        column: index + 1,
        categories: [] as ShopperProducts.schemas['Category'][],
    }));

    categories.forEach((category) => {
        groups[getDesktopNavColumn(category) - 1]?.categories.push(category);
    });

    return groups;
}

const topLevelNavigationItemClassName =
    'w-full border-b-2 border-transparent px-0 font-semibold uppercase tracking-nav !transition-none !duration-0 hover:border-accent-secondary focus-visible:border-accent-secondary data-[state=open]:border-accent-secondary rounded-none';
const desktopNavigationMenuLinkNonInteractiveClassName =
    "data-[active=true]:bg-header-menu-active-background [&_svg:not([class*='text-'])]:text-header-menu-icon flex flex-col gap-1 rounded-sm p-2 text-sm transition-all outline-none [&_svg:not([class*='size-'])]:size-4";
const desktopNavigationNoTransitionClassName = '!transition-none !duration-0';
const desktopNavigationContentNoAnimationClassName =
    '!transition-none !duration-0 data-[motion^=from-]:!animate-none data-[motion^=from-]:!duration-0 data-[motion^=to-]:!animate-none data-[motion^=to-]:!duration-0 group-data-[viewport=false]/navigation-menu:data-[state=open]:!animate-none group-data-[viewport=false]/navigation-menu:data-[state=closed]:!animate-none group-data-[viewport=false]/navigation-menu:!duration-0';
const desktopNavigationViewportNoAnimationClassName =
    '!transition-none !duration-0 data-[state=open]:!animate-none data-[state=closed]:!animate-none';

function CategoryNavigationMenuDesktopLink({
    itemCtx,
    renderElement,
    className,
    children,
    ...props
}: ComponentProps<typeof NavigationMenuLink> & {
    itemCtx: CategoryNavigationMenuDesktopListItemCtx;
    renderElement?: SlotType<CategoryNavigationMenuDesktopListItemCtx>;
}) {
    // ## SEO url changes ## — read allowOverrideCategoryUrls from config
    const config = useConfig<AppConfig>();
    const allowOverride = config?.features?.allowOverrideCategoryUrls ?? false;
    const customContent = renderSlot(renderElement, itemCtx);
    if (isValidElement(customContent)) {
        return customContent;
    }

    const content = children ?? customContent ?? itemCtx.category.name;
    const linkClassName = cn(className, desktopNavigationNoTransitionClassName);

    if (!isCategoryLinkable(itemCtx.category)) {
        const spanClassName = withoutInteractiveClassNames(
            cn(desktopNavigationMenuLinkNonInteractiveClassName, linkClassName)
        );

        // `NavigationMenuLink` props extend anchor props (plus the Radix-specific `active`),
        // which are not assignable to a plain <span>. Forward only the attributes that
        // <span> and <a> share (the generic HTML attributes carried by slot props).
        const spanProps = props as HTMLAttributes<HTMLSpanElement>;

        return (
            <span {...spanProps} data-slot="navigation-menu-link" className={spanClassName}>
                {content}
            </span>
        );
    }

    return (
        <NavigationMenuLink {...props} className={linkClassName} asChild>
            <NavLink to={getCategoryUrl(config, itemCtx.category, allowOverride)}>{content}</NavLink>
        </NavigationMenuLink>
    );
}

function CategoryNavigationMenuDesktopNested({
    category,
    level,
    maxDepth,
    path,
    propsElement,
    renderElement,
}: Pick<CategoryNavigationMenuDesktopProps, 'propsElement' | 'renderElement'> & {
    category: ShopperProducts.schemas['Category'];
    level: number;
    maxDepth: number;
    path: ReadonlyArray<ShopperProducts.schemas['Category']>;
}) {
    if (!hasChildren(category) || level >= maxDepth) {
        return null;
    }

    const childPath = [...path, category];
    const children = getNavigationChildren(category);
    const length = children.length;

    return (
        <ul className="flex flex-col gap-1">
            {children.map((childCategory, index) => {
                const itemCtx: CategoryNavigationMenuDesktopListItemCtx = {
                    category: childCategory,
                    parent: category,
                    level,
                    path: childPath,
                    index,
                    isFirst: index === 0,
                    isLast: index === length - 1,
                    isLeaf: !hasChildren(childCategory) || maxDepth === level + 1,
                };

                return (
                    <li key={childCategory.id}>
                        <CategoryNavigationMenuDesktopLink
                            {...propsFor(propsElement, itemCtx)}
                            itemCtx={itemCtx}
                            renderElement={renderElement}
                            tabIndex={-1}
                            className={cn(
                                'w-full rounded-sm px-0 py-1 text-sm leading-5 text-header-menu-foreground hover:bg-transparent hover:text-[var(--header-menu-hover-foreground)] hover:underline hover:underline-offset-2 focus:bg-transparent focus:text-[var(--header-menu-hover-foreground)] focus:underline focus:underline-offset-2',
                                propsFor(propsElement, itemCtx).className
                            )}
                        />
                        {!itemCtx.isLeaf && (
                            <CategoryNavigationMenuDesktopNested
                                category={childCategory}
                                level={level + 1}
                                maxDepth={maxDepth}
                                path={childPath}
                                propsElement={propsElement}
                                renderElement={renderElement}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function getPanelItems(triggerEl: HTMLElement): HTMLElement[] {
    const item = triggerEl.closest('[data-slot="navigation-menu-item"]');
    return Array.from(
        item?.querySelectorAll<HTMLElement>(
            '[data-slot="navigation-menu-content"] a[href], [data-slot="navigation-menu-content"] button:not([disabled])'
        ) ?? []
    );
}

function handleTriggerArrowNav(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = getPanelItems(e.currentTarget);
    if (!items.length) return;
    (e.key === 'ArrowDown' ? items[0] : items[items.length - 1])?.focus();
}

function handlePanelArrowNav(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
}

function CategoryNavigationMenuDesktopPanel({
    itemCtx,
    maxDepth,
    propsContent,
    propsElement,
    renderSlotContentAfter,
    renderElement,
}: Pick<
    CategoryNavigationMenuDesktopProps,
    'propsContent' | 'propsElement' | 'renderSlotContentAfter' | 'renderElement'
> & {
    itemCtx: CategoryNavigationMenuDesktopListItemCtx;
    maxDepth: number;
}) {
    // Subscribe to the SubCategoryStore for this top-level category's enriched data.
    // Re-renders only when the store entry for `itemCtx.category.id` changes identity —
    // i.e. the panel for THIS category gets fresh subcategory data while all other
    // top-level items remain untouched.
    const enrichedCategory = useSubCategory(itemCtx.category.id) ?? itemCtx.category;
    const childCategories = getNavigationChildren(enrichedCategory);

    const marketingBannerCategory = childCategories.find(
        (cat) => (cat as NavigationOverrideCategory).c_isMarketingBanner === true && isCategoryVisible(cat)
    ) as NavigationOverrideCategory | undefined;
    const visibleChildCategories = marketingBannerCategory
        ? childCategories.filter((cat) => (cat as NavigationOverrideCategory).c_isMarketingBanner !== true)
        : childCategories;

    const columns = groupCategoriesByColumn(visibleChildCategories);

    const effectiveItemCtx = marketingBannerCategory
        ? {
              ...itemCtx,
              category: {
                  ...itemCtx.category,
                  c_navigationBannerImage: marketingBannerCategory.c_navigationBannerImage,
                  c_navigationBannerLink: marketingBannerCategory.c_navigationBannerLink,
              } as ShopperProducts.schemas['Category'],
          }
        : itemCtx;

    return (
        <div
            {...propsFor(propsContent, effectiveItemCtx)}
            className={cn(
                'flex w-screen items-start justify-center gap-6 border-t border-header-menu-border bg-header-menu-background section-container py-4 text-header-menu-foreground',
                propsFor(propsContent, effectiveItemCtx).className
            )}
            onKeyDown={handlePanelArrowNav}>
            <div
                className="grid min-w-0 flex-1 gap-6"
                style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
                {columns.map((column) => (
                    <ul key={column.column} className="flex min-w-0 flex-col gap-6">
                        {column.categories.map((category, index) => {
                            const childCtx: CategoryNavigationMenuDesktopListItemCtx = {
                                category,
                                parent: itemCtx.category,
                                level: 1,
                                path: [itemCtx.category],
                                index,
                                isFirst: index === 0,
                                isLast: index === column.categories.length - 1,
                                isLeaf: !hasChildren(category) || maxDepth === 2,
                            };

                            return (
                                <li key={category.id} className="flex flex-col gap-2">
                                    <CategoryNavigationMenuDesktopLink
                                        {...propsFor(propsElement, childCtx)}
                                        itemCtx={childCtx}
                                        renderElement={renderElement}
                                        tabIndex={-1}
                                        className={cn(
                                            'w-full rounded-sm px-0 py-1 text-xs font-semibold uppercase leading-4 tracking-nav text-header-menu-foreground hover:bg-transparent hover:text-[var(--header-menu-hover-foreground)] hover:underline hover:underline-offset-2 focus:bg-transparent focus:text-[var(--header-menu-hover-foreground)] focus:underline focus:underline-offset-2',
                                            propsFor(propsElement, childCtx).className
                                        )}
                                    />
                                    {!childCtx.isLeaf && (
                                        <CategoryNavigationMenuDesktopNested
                                            category={category}
                                            level={2}
                                            maxDepth={maxDepth}
                                            path={childCtx.path}
                                            propsElement={propsElement}
                                            renderElement={renderElement}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ))}
            </div>

            {renderSlot(renderSlotContentAfter, effectiveItemCtx)}
        </div>
    );
}

export default function CategoryNavigationMenuDesktop({
    categories,
    maxDepth = Number.POSITIVE_INFINITY,
    viewport = true,
    ...props
}: CategoryNavigationMenuDesktopProps) {
    if (maxDepth <= 0) {
        throw new Error('maxDepth must be greater than 0');
    }
    const [activeValue, setActiveValue] = useState<string>('');
    const navigate = useNavigate();
    // ## SEO url changes ## — read allowOverrideCategoryUrls from config
    const config = useConfig<AppConfig>();
    const allowOverride = config?.features?.allowOverrideCategoryUrls ?? false;
    const visibleCategories = getVisibleCategories(categories);

    if (!visibleCategories.length) {
        return null;
    }

    const listCtx: CategoryNavigationMenuDesktopListCtx = {
        level: 0,
        path: [],
        parent: undefined,
        categories: visibleCategories,
    };
    const {
        propsViewport,
        propsList,
        propsListItem,
        propsElement,
        propsContent,
        propsContentContainer,
        renderSlotListBefore,
        renderSlotListAfter,
        renderSlotListItemBefore,
        renderSlotListItemAfter,
        renderSlotContentAfter,
        renderElement,
        ...rest
    } = props;
    const length = visibleCategories.length;
    const listProps = propsFor(propsList, listCtx);
    const viewportProps = propsFor(propsViewport, listCtx);

    return (
        <NavigationMenu
            {...rest}
            className={cn('static block w-full max-w-none mb-1', rest.className)}
            viewport={false}
            data-viewport={viewport}
            value={activeValue}
            onValueChange={setActiveValue}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setActiveValue('');
                }
            }}>
            {renderSlot(renderSlotListBefore, listCtx)}

            <NavigationMenuList {...listProps} className={cn('flex w-full justify-between gap-0', listProps.className)}>
                {visibleCategories.map((category, index) => {
                    const itemCtx: CategoryNavigationMenuDesktopListItemCtx = {
                        category,
                        parent: undefined,
                        level: 0,
                        path: [],
                        index,
                        isFirst: index === 0,
                        isLast: index === length - 1,
                        isLeaf: !hasChildren(category) || maxDepth === 1,
                    };

                    const contentContainerProps = propsFor(propsContentContainer, itemCtx);
                    const elementProps = propsFor(propsElement, itemCtx);
                    const listItemProps = propsFor(propsListItem, itemCtx);
                    const triggerContent = renderSlot(renderElement ?? category.name, itemCtx);

                    return (
                        <Fragment key={category.id}>
                            {renderSlot(renderSlotListItemBefore, itemCtx)}

                            <NavigationMenuItem
                                {...listItemProps}
                                value={category.id}
                                className={cn('block min-w-0 text-center', listItemProps.className)}>
                                {itemCtx.isLeaf ? (
                                    <CategoryNavigationMenuDesktopLink
                                        {...elementProps}
                                        onFocus={(e) => {
                                            setActiveValue('');
                                            elementProps.onFocus?.(e);
                                        }}
                                        itemCtx={itemCtx}
                                        renderElement={renderElement}
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            topLevelNavigationItemClassName,
                                            elementProps.className
                                        )}
                                    />
                                ) : (
                                    <>
                                        {isCategoryLinkable(category) ? (
                                            <NavigationMenuTrigger
                                                {...(elementProps as ComponentProps<typeof NavigationMenuTrigger>)}
                                                className={cn(topLevelNavigationItemClassName, elementProps.className)}
                                                onFocus={() => setActiveValue(category.id)}
                                                asChild>
                                                <NavLink
                                                    to={getCategoryUrl(config, category, allowOverride)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            void navigate(
                                                                getCategoryUrl(config, category, allowOverride)
                                                            );
                                                        }
                                                        handleTriggerArrowNav(e);
                                                    }}>
                                                    {triggerContent}
                                                </NavLink>
                                            </NavigationMenuTrigger>
                                        ) : (
                                            <NavigationMenuTrigger
                                                {...(elementProps as ComponentProps<typeof NavigationMenuTrigger>)}
                                                className={cn(topLevelNavigationItemClassName, elementProps.className)}
                                                onFocus={() => setActiveValue(category.id)}
                                                onKeyDown={handleTriggerArrowNav}>
                                                {triggerContent}
                                            </NavigationMenuTrigger>
                                        )}
                                        <NavigationMenuContent
                                            {...contentContainerProps}
                                            className={cn(
                                                contentContainerProps.className,
                                                desktopNavigationContentNoAnimationClassName
                                            )}>
                                            <CategoryNavigationMenuDesktopPanel
                                                itemCtx={itemCtx}
                                                maxDepth={maxDepth}
                                                propsContent={propsContent}
                                                propsElement={propsElement}
                                                renderSlotContentAfter={renderSlotContentAfter}
                                                renderElement={renderElement}
                                            />
                                        </NavigationMenuContent>
                                    </>
                                )}
                            </NavigationMenuItem>

                            {renderSlot(renderSlotListItemAfter, itemCtx)}
                        </Fragment>
                    );
                })}
            </NavigationMenuList>

            {renderSlot(renderSlotListAfter, listCtx)}

            {viewport && (
                <NavigationMenuViewport
                    {...viewportProps}
                    className={cn(viewportProps.className, desktopNavigationViewportNoAnimationClassName)}
                />
            )}
        </NavigationMenu>
    );
}

/**
 * Provides category navigation data to desktop navigation children.
 *
 * This local implementation keeps `navigation-menu-desktop` independent from the legacy `navigation-menu` component
 * so the older component can be deleted without removing async category resolution support.
 */
export function WithCategoryNavigationMenu({
    resolve: rootCategoryPromise,
    defer: subCategoriesPromise,
    fallback,
    errorElement,
    children,
    ...props
}: WithCategoryNavigationMenuProps) {
    return rootCategoryPromise ? (
        <Suspense fallback={fallback}>
            <Await resolve={rootCategoryPromise} errorElement={errorElement}>
                {(rootCategory) => (
                    <WithCategoryNavigationMenuView {...props} root={rootCategory} defer={subCategoriesPromise}>
                        {children}
                    </WithCategoryNavigationMenuView>
                )}
            </Await>
        </Suspense>
    ) : null;
}
